import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';

// ---------------------------------------------------------------------------
// ProgressBar – horizontal progress indicator with ARIA support
// ---------------------------------------------------------------------------

/**
 * Color theme options for the progress bar
 */
export type ProgressBarColor =
  | 'emerald'
  | 'blue'
  | 'violet'
  | 'amber'
  | 'teal'
  | 'red'
  | 'green'
  | 'indigo'
  | 'gray';

/**
 * Size options for the progress bar height
 */
export type ProgressBarSize = 'xs' | 'sm' | 'md' | 'lg';

interface ProgressBarSignature {
  Args: {
    /** Current value (0 to max) */
    value: number;
    /** Maximum value (default: 100) */
    max?: number;
    /** Color theme */
    color?: ProgressBarColor;
    /** Size variant */
    size?: ProgressBarSize;
    /** Use gradient fill instead of solid */
    gradient?: boolean;
    /** Custom aria-label */
    label?: string;
  };
  Element: HTMLDivElement;
}

const colorClasses: Record<ProgressBarColor, { solid: string; gradient: string }> = {
  emerald: { solid: 'bg-emerald-500', gradient: 'bg-gradient-to-r from-emerald-400 to-teal-500' },
  blue: { solid: 'bg-blue-500', gradient: 'bg-gradient-to-r from-blue-400 to-indigo-500' },
  violet: { solid: 'bg-violet-500', gradient: 'bg-gradient-to-r from-violet-400 to-purple-500' },
  amber: { solid: 'bg-amber-500', gradient: 'bg-gradient-to-r from-amber-400 to-orange-500' },
  teal: { solid: 'bg-teal-500', gradient: 'bg-gradient-to-r from-teal-400 to-cyan-500' },
  red: { solid: 'bg-red-500', gradient: 'bg-gradient-to-r from-red-400 to-rose-500' },
  green: { solid: 'bg-green-500', gradient: 'bg-gradient-to-r from-green-400 to-emerald-500' },
  indigo: { solid: 'bg-indigo-500', gradient: 'bg-gradient-to-r from-indigo-400 to-violet-500' },
  gray: { solid: 'bg-gray-500', gradient: 'bg-gradient-to-r from-gray-400 to-slate-500' },
};

const sizeClasses: Record<ProgressBarSize, string> = {
  xs: 'h-1',
  sm: 'h-2',
  md: 'h-3',
  lg: 'h-4',
};

/**
 * ProgressBar - A horizontal progress bar with ARIA support
 *
 * Displays a progress indicator with customizable color, size, and
 * optional gradient fill. Fully accessible with ARIA progressbar role.
 *
 * @example Basic usage
 * ```gts
 * import ProgressBar from 'tio-reimagined/components/progress-bar';
 *
 * <ProgressBar @value={{75}} @color="emerald" />
 * ```
 *
 * @example With custom max and size
 * ```gts
 * <ProgressBar @value={{42}} @max={{100}} @color="violet" @size="lg" />
 * ```
 *
 * @example Gradient style
 * ```gts
 * <ProgressBar @value={{60}} @gradient={{true}} @color="blue" />
 * ```
 */
export default class ProgressBar extends Component<ProgressBarSignature> {
  get max() {
    const max = this.args.max ?? 100;
    return max > 0 ? max : 100;
  }

  get percent() {
    const value = Math.max(0, Math.min(this.args.value, this.max));
    return Math.round((value / this.max) * 100);
  }

  get widthStyle() {
    return htmlSafe(`width: ${this.percent}%`);
  }

  get color() {
    return this.args.color ?? 'blue';
  }

  get fillClass() {
    const colors = colorClasses[this.color];
    return this.args.gradient ? colors.gradient : colors.solid;
  }

  get sizeClass() {
    return sizeClasses[this.args.size ?? 'sm'];
  }

  get ariaLabel() {
    return this.args.label ?? `${this.percent}% complete`;
  }

  <template>
    <div
      data-test-progress-bar
      class="{{this.sizeClass}} bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden"
      role="progressbar"
      aria-valuenow={{this.percent}}
      aria-valuemin={{0}}
      aria-valuemax={{100}}
      aria-label={{this.ariaLabel}}
      ...attributes
    >
      <div
        data-test-progress-bar-fill
        class="h-full {{this.fillClass}} rounded-full transition-all duration-300"
        style={{this.widthStyle}}
      ></div>
    </div>
  </template>
}

export type { ProgressBarSignature };
